import { EvmTransfer, SolanaTransfer } from '@/types';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';

interface HistoryProps {
  transactions: (EvmTransfer | SolanaTransfer)[];
  twitterUsername?: string;
  userSolanaAddress: string;
  profileImageUrl?: string;
}

interface TransactionGroup {
  request?: string;
  x_post_id?: string;
  transactions: (EvmTransfer | SolanaTransfer)[];
}

function getExplorerUrl(tx: EvmTransfer | SolanaTransfer): string {
    if ('chain' in tx && 'hash' in tx) {
        const chainId = parseInt(tx.chain, 16) 
        switch (chainId) {
            case 8453:
                return `https://basescan.org/tx/${tx.hash}`;
            case 1:
                return `https://etherscan.io/tx/${tx.hash}`;
            case 1151111081099710:
                return `https://solscan.io/tx/${tx.hash}`;
            default:
                return `https://etherscan.io/tx/${tx.hash}`;
        }
    }
    return '';
}

function getChainName(tx: EvmTransfer | SolanaTransfer): string {
  if ('chain' in tx) {
    const chainId = parseInt(tx.chain, 16) 
    switch (chainId) {
      case 8453:
        return 'Base';
      case 1:
        return 'Ethereum';
      case 1151111081099710:
        return 'Solana';
      default:
        return `Chain ${chainId}`;
    }
  }
  return 'Unknown Chain';
}

function cleanSummaryText(tx: EvmTransfer | SolanaTransfer, userAddress: string): string | undefined {
  if (!tx.summary) return undefined;

  let summary = tx.summary;

  // Handle swap case
  if (summary.includes('swapped') && summary.includes(userAddress)) {
    summary = summary.replace(`${userAddress} swapped`, 'Swapped');
  }

  // Handle transfer case
  if (summary.includes('transferred')) {
    const fromMatch = summary.match(/^(.*?) transferred (.*?) to (.*?)\.?$/);
    if (fromMatch) {
      const [_, fromAddr, amount, toAddr] = fromMatch;
      if (fromAddr === userAddress) {
        summary = `Sent ${amount} to ${toAddr.slice(0, 4)}...${toAddr.slice(-4)}`;
      } else if (toAddr === userAddress) {
        summary = `Received ${amount} from ${fromAddr.slice(0, 4)}...${fromAddr.slice(-4)}`;
      }
    }
  }

  // Replace token addresses with symbols if record exists
  if (tx.record?.input) {
    const { input } = tx.record;
    if (input.fromToken?.address) {
      summary = summary.replace(
        new RegExp(input.fromToken.address, 'gi'), 
        input.fromToken.symbol.toUpperCase()
      );
    }
    if (input.toToken?.address) {
      summary = summary.replace(
        new RegExp(input.toToken.address, 'gi'), 
        input.toToken.symbol.toUpperCase()
      );
    }
  }

  return summary;
}

export function History({ transactions, twitterUsername, userSolanaAddress, profileImageUrl }: HistoryProps) {
  const filteredTransactions = transactions.filter(tx => 
    !tx.summary?.includes('SOL to multiple accounts') &&
    !(tx.summary?.toLowerCase().includes('minted') && tx.summary?.toLowerCase().includes('compressed nft'))
  );

  // Group transactions by x_post_id
  const groupedTransactions = filteredTransactions.reduce<TransactionGroup[]>((groups, tx) => {
    if (tx.record?.x_post_id && tx.record?.request) {
      const existingGroup = groups.find(g => g.x_post_id === tx.record?.x_post_id);
      if (existingGroup) {
        existingGroup.transactions.push(tx);
      } else {
        groups.push({
          request: tx.record?.request,
          x_post_id: tx.record.x_post_id,
          transactions: [tx]
        });
      }
    } else {
      groups.push({ transactions: [tx] });
    }
    return groups;
  }, []);

  return (
    <div className="bg-white shadow sm:rounded-lg overflow-hidden">
      <ul role="list" className="divide-y divide-gray-200">
        {groupedTransactions.map((group, index) => (
          <li 
            key={group.x_post_id || index}
            className="hover:bg-gray-50 transition-colors duration-150 ease-in-out"
          >
            <div className="px-4 py-4 sm:px-6">
              <div className="flex flex-col gap-2">
                {group.request && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {profileImageUrl && (
                        <img 
                          src={profileImageUrl} 
                          alt="Profile" 
                          className="w-8 h-8 rounded-full"
                        />
                      )}
                      {group.x_post_id && twitterUsername ? (
                        <a
                          href={`https://x.com/${twitterUsername}/status/${group.x_post_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-lg text-gray-900 hover:text-gray-600 transition-colors flex items-center gap-1"
                        >
                          {group.request}
                        </a>
                      ) : (
                        <div className="text-lg text-gray-900">
                          {group.request}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                <div className="flex flex-col gap-1">
                  {group.transactions.map(tx => (
                    <div key={tx.hash} className="flex items-center justify-between">
                      <div className="flex items-center gap-2 max-w-2xl overflow-hidden">
                        <a 
                          href={getExplorerUrl(tx)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-gray-900 hover:text-gray-600 transition-colors"
                        >
                          {cleanSummaryText(tx, userSolanaAddress)}
                        </a>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 flex-shrink-0">
                          {getChainName(tx)}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 ml-4 flex-shrink-0">
                        {new Date(tx.block_timestamp).toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
} 